import React, { useState, useCallback, memo, useEffect } from 'react';
import { getBezierPath, EdgeProps, useReactFlow } from '@xyflow/react';
import { useCanvasStore } from '@/stores/canvasStore';
import { useEdgeActiveStore } from '@/stores/edgeActiveStore';
import { setParticlePath, removeParticlePath } from './EdgeParticlesCanvas';

const CustomEdgeComponent: React.FC<EdgeProps> = ({
  id,
  source,
  target,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style,
}) => {
  const { setEdges } = useReactFlow();
  const [isHovered, setIsHovered] = useState(false);
  const [isSelected, setIsSelected] = useState(false);

  // P0-2: 精确选择器，只有自己 isActive 变化才重渲染
  const isActive = useEdgeActiveStore(useCallback((state) => state.activeEdgeIds.has(id), [id]));

  const edgeDeselectCounter = useCanvasStore((s) => s.edgeDeselectCounter);

  useEffect(() => {
    setIsSelected(false);
    setIsHovered(false);
  }, [edgeDeselectCounter]);

  const [edgePath] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  const showHighlight = isHovered || isSelected;
  const showParticles = isActive && !showHighlight;

  // 注册/更新路径到 Canvas 粒子注册表
  // 用 useEffect（异步）而非 useLayoutEffect（同步），避免阻塞浏览器绘制
  // useLayoutEffect 在 React commit 阶段同步执行，多个 CustomEdge 同时重渲染时
  // 多个 useLayoutEffect 同步执行会阻塞 rAF → 光流卡顿
  // useEffect 在浏览器绘制后异步执行，不阻塞 rAF
  // 无 cleanup：edgePath 变化时只 update（setParticlePath 内部处理），不 remove + set
  useEffect(() => {
    if (showParticles) {
      setParticlePath(id, edgePath);
    } else {
      removeParticlePath(id);
    }
  }, [id, edgePath, showParticles]);

  // 组件 unmount 时 remove 路径（独立 effect，避免 edgePath 变化时触发 remove）
  useEffect(() => {
    return () => { removeParticlePath(id); };
  }, [id]);

  const handleEdgeClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsSelected(true);
  }, []);

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (!isSelected) {
      setIsHovered(false);
    }
  }, [isSelected]);

  const handleDeleteClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setEdges((eds) => eds.filter((edge) => edge.id !== id));
    setIsSelected(false);
  }, [id, setEdges]);

  const midX = (sourceX + targetX) / 2;
  const midY = (sourceY + targetY) / 2;

  return (
    <g className="react-flow__edge" style={style}>
      <path
        d={edgePath}
        className="react-flow__edge-path"
        style={{
          stroke: showHighlight ? '#ffffff' : '#ffffff',
          strokeWidth: showHighlight ? 3 : 2,
          fill: 'none',
          strokeOpacity: showHighlight ? 1 : 0.4,
          cursor: 'pointer',
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleEdgeClick}
      />
      {/* 粒子已移至 Canvas 2D 渲染（EdgeParticlesCanvas），消除 90+ SVG path 的 DOM 开销和 stroke-dashoffset 动画频闪 */}
      {isSelected && (
        <g
          className="edge-delete-btn"
          style={{ cursor: 'pointer' }}
          onClick={handleDeleteClick}
        >
          <circle
            cx={midX}
            cy={midY}
            r={12}
            fill="#ef4444"
            stroke="#ffffff"
            strokeWidth={2}
          />
          <line
            x1={midX - 5}
            y1={midY - 5}
            x2={midX + 5}
            y2={midY + 5}
            stroke="#ffffff"
            strokeWidth={2}
            strokeLinecap="round"
          />
          <line
            x1={midX + 5}
            y1={midY - 5}
            x2={midX - 5}
            y2={midY + 5}
            stroke="#ffffff"
            strokeWidth={2}
            strokeLinecap="round"
          />
        </g>
      )}
    </g>
  );
};

export const CustomEdge = memo(CustomEdgeComponent);
